import React, { useRef, useEffect, useLayoutEffect } from 'react'
import styles from './ConversationWindow.module.scss'
import MessageList from '../MessageList/MessageList.tsx'
import { Message } from '../../../../../utils/conversationStorage.ts'
import type { DesktopSuggestion } from '../../dashboardActionsConfig.tsx'

const PIN_PX = 100

const scrollContainerToBottom = (el: HTMLElement) => {
  el.scrollTop = el.scrollHeight
}

const isNearBottom = (el: HTMLElement) =>
  el.scrollHeight - el.scrollTop - el.clientHeight < PIN_PX

interface ConversationWindowProps {
  messages: Message[]
  onClose: () => void
  isLoading?: boolean
  matchedSuggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  allSuggestions?: DesktopSuggestion[]
  hasReachedLimit?: boolean
  remainingMessages?: number
  limit?: number
  userData?: any
  pendingImagePreview?: string | null
  isLatestMessageGuardarPost?: boolean
  showDocumentPipelineLoop?: boolean
  documentPipelinePhase?: string | null
  variant?: 'dashboard' | 'floating'
  onSuggestedChipSend?: (label: string, actionId: string) => void
}

const ConversationWindow: React.FC<ConversationWindowProps> = ({
  messages,
  onClose,
  isLoading = false,
  matchedSuggestion,
  onSuggestionAction,
  onRequestGuide,
  allSuggestions = [],
  hasReachedLimit = false,
  remainingMessages = 0,
  limit = 0,
  userData,
  pendingImagePreview = null,
  isLatestMessageGuardarPost = false,
  showDocumentPipelineLoop = false,
  documentPipelinePhase = null,
  variant = 'dashboard',
  onSuggestedChipSend
}) => {
  void onClose
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const prevScrollSigRef = useRef('')

  const last = messages[messages.length - 1]
  const scrollSig = `${messages.length}:${last?.id ?? ''}:${isLoading}:${documentPipelinePhase ?? ''}`

  const scrollportClass =
    variant === 'floating'
      ? `${styles.messagesScrollport} ${styles.messagesScrollportFloating}`
      : `${styles.messagesScrollport} ${styles.messagesScrollportDashboard}`

  const paddedClass =
    variant === 'floating' ? styles.messagesPaddedFloating : styles.messagesPaddedDashboard

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    if (scrollSig !== prevScrollSigRef.current) {
      prevScrollSigRef.current = scrollSig
      scrollContainerToBottom(el)
    }
  }, [scrollSig])

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return
    const ro = new ResizeObserver(() => {
      if (isNearBottom(container)) {
        scrollContainerToBottom(container)
      }
    })
    ro.observe(content)
    return () => ro.disconnect()
  }, [])

  return (
    <div ref={containerRef} className={scrollportClass}>
      <div ref={contentRef} className={`${styles.messagesPadded} ${paddedClass}`}>
        <MessageList
          messages={messages}
          isLoading={isLoading}
          matchedSuggestion={matchedSuggestion}
          onSuggestionAction={onSuggestionAction}
          onRequestGuide={onRequestGuide}
          allSuggestions={allSuggestions}
          hasReachedLimit={hasReachedLimit}
          remainingMessages={remainingMessages}
          limit={limit}
          userData={userData}
          pendingImagePreview={pendingImagePreview}
          isLatestMessageGuardarPost={isLatestMessageGuardarPost}
          showDocumentPipelineLoop={showDocumentPipelineLoop}
          documentPipelinePhase={documentPipelinePhase}
          onSuggestedChipSend={onSuggestedChipSend}
        />
      </div>
    </div>
  )
}

export default ConversationWindow
